/**
 * Der Nachlauf des Speicherns: vorgemerkte Dateien an die frisch angelegten
 * Risikoobjekte hängen.
 *
 * Warum überhaupt ein Nachlauf? Beim Neuanlegen entsteht das RiskObject erst
 * im Submit (`POST /risk-objects/bulk`); zum Zeitpunkt der Dateiauswahl gibt es
 * noch keine Id. Der Puffer parkt deshalb `File`-Objekte, und hier werden sie
 * — **streng sequenziell** — hochgeladen.
 *
 * Sequenziell, nicht parallel: der Rate-Limit-Bucket des Upload-Endpunkts gilt
 * **pro IP** und wird hinter dem zentralen Proxy von allen Nutzern des Büros
 * geteilt. Bei 429 wird `RateLimit-Reset` ausgewertet und danach erneut
 * versucht; ein fixer Abstand reicht rechnerisch nicht.
 *
 * Bewusst DOM-frei und mit injizierbaren Abhängigkeiten, damit der Node-Runner
 * die Reihenfolge- und Backoff-Logik prüfen kann.
 */
import { documentService, UploadError } from "../../services/documentService";
import { GLOBAL_SCOPE, slotTag, type PendingUpload, type UploadScope } from "./uploadBuffer";

export type UploadFehler = {
  /** Feldbeschriftung, an der die Datei hing. */
  label: string;
  fileName: string;
  grund: string;
};

export type UploadErgebnis = {
  erfolgreich: number;
  fehler: UploadFehler[];
};

/**
 * Scope → RiskObject-Id. `createMany` liefert die angelegten Objekte in der
 * Reihenfolge der übergebenen items (`riskObject.service.ts`), jedes Gebäude
 * ist also auf „sein" RiskObject abbildbar.
 *
 * **Defensiv:** `createMany` liest jedes Objekt nach dem Insert erneut und
 * verwirft nicht auffindbare Sätze — die Liste kann also kürzer sein als die
 * Gebäudeliste. Dann ist die Zuordnung nicht mehr vertrauenswürdig und alles
 * geht ans erste Objekt, statt Dateien am falschen Gebäude abzulegen.
 */
export const zieleFuerGebaeude = (
  gebaeudeIds: UploadScope[],
  createdIds: number[],
): Map<UploadScope, number> => {
  const map = new Map<UploadScope, number>();
  if (createdIds.length === 0) return map;
  const passt = createdIds.length === gebaeudeIds.length;
  gebaeudeIds.forEach((id, idx) => {
    map.set(id, passt ? createdIds[idx] : createdIds[0]);
  });
  return map;
};

const schlafen = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Wartezeit vor dem nächsten Versuch: Serverangabe schlägt den Default. */
export const backoffMs = (versuch: number, retryAfterSeconds: number | null): number => {
  if (retryAfterSeconds != null && retryAfterSeconds > 0) {
    // Eine halbe Sekunde Sicherheitsabstand — das Fenster ist sekundengenau.
    return Math.min(retryAfterSeconds * 1000 + 500, 60_000);
  }
  return Math.min(2000 * 2 ** (versuch - 1), 30_000);
};

const MAX_VERSUCHE = 4;

export type LadeParams = {
  pending: PendingUpload[];
  /** Scope → RiskObject-Id, siehe {@link zieleFuerGebaeude}. */
  ziele: Map<UploadScope, number>;
  /**
   * Ziel der Slots auf **Objektebene** (`GLOBAL_SCOPE`, z. B. das Objektfoto) —
   * üblicherweise das erste angelegte Gebäude. Bewusst KEIN allgemeiner
   * Notnagel für unbekannte Scopes: ein Gebäude-Scope ohne Treffer landete
   * damit stillschweigend am falschen Gebäude.
   */
  fallbackRiskObjectId: number;
  contactId?: number | null;
  onFortschritt?: (erledigt: number, gesamt: number) => void;
  /** Test-Naht. */
  upload?: typeof documentService.upload;
  /** Test-Naht. */
  warte?: (ms: number) => Promise<void>;
};

export const ladeVorgemerkteHoch = async ({
  pending,
  ziele,
  fallbackRiskObjectId,
  contactId,
  onFortschritt,
  upload = documentService.upload.bind(documentService),
  warte = schlafen,
}: LadeParams): Promise<UploadErgebnis> => {
  const gesamt = pending.length;
  let erfolgreich = 0;
  const fehler: UploadFehler[] = [];

  for (let i = 0; i < pending.length; i += 1) {
    const p = pending[i];
    // Objektebene → Fallback; ein Gebäude-Scope MUSS ein eigenes Ziel haben.
    // Findet sich keines (z. B. weil das Gebäude nach der Dateiauswahl gelöscht
    // wurde), wird das gemeldet statt die Datei am ersten Gebäude abzulegen.
    const riskObjectId =
      p.scope === GLOBAL_SCOPE ? fallbackRiskObjectId : ziele.get(p.scope);
    if (riskObjectId == null) {
      fehler.push({
        label: p.label,
        fileName: p.file.name,
        grund: "Das zugehörige Gebäude wurde nicht angelegt.",
      });
      onFortschritt?.(i + 1, gesamt);
      continue;
    }
    let versuch = 0;
    // Nur 429 wird wiederholt. 413/400/404 sind endgültig — ein zweiter
    // Versuch änderte am Ergebnis nichts und verbrauchte nur Kontingent.
    for (;;) {
      versuch += 1;
      try {
        await upload({
          file: p.file,
          riskObjectId,
          contactId: contactId ?? undefined,
          tags: [slotTag(p.key)],
        });
        erfolgreich += 1;
        break;
      } catch (err) {
        const istRate = err instanceof UploadError && err.status === 429;
        if (istRate && versuch < MAX_VERSUCHE) {
          await warte(backoffMs(versuch, (err as UploadError).retryAfterSeconds));
          continue;
        }
        fehler.push({
          label: p.label,
          fileName: p.file.name,
          grund:
            err instanceof Error && err.message
              ? err.message
              : "Upload fehlgeschlagen.",
        });
        break;
      }
    }
    onFortschritt?.(i + 1, gesamt);
  }

  return { erfolgreich, fehler };
};
